import GoogleMapReact, { Coords } from 'google-map-react';
import gsap from 'gsap';
import config from 'config/config';
import {
  Box,
  Center,
  Flex,
  forwardRef,
  HStack,
  ScaleFade,
  Spacer,
  Tooltip,
  useDisclosure,
  Button as ChakraButton,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AddressSelector,
  AddressGeoCodeSelector,
  MapDetailSelector,
  PanelSelector,
  RoofAngleSelector,
  ConsumptionSelector,
  BillSelector,
  AddressPostcodeSelector,
} from 'store/selectors';
import Button from 'ui/molecules/Button';
import PanelLine from 'features/map/PanelLine';
import {
  calculatePanelGeoPoint,
  getDistanceBetweenTwoPoints,
  getNumPanels,
  metersPerPx,
} from 'features/map/mapUtils';
import {
  AddPanelsIcon,
  NorthIcon,
  PanelLineGrabIcon,
  RemovePanelsIcon,
  RoofIcon,
  RotatePanelIcon,
  LightbulbIcon,
} from 'ui/icons/Icons';
import IconButton from 'ui/molecules/IconButton';
import { RootState } from 'store/store';
import { updateMapDetails } from 'store/answers/answersSlice';
import AppRoutes from 'config/AppRoutes';
import PageLayout from 'ui/layout/PageLayout';
// import useAddressGeoPoint from 'hooks/useAddressGeoPoint';
import getUUID from 'utils/uuid';
import {
  calculateConsumptionBaseOnBill,
  getEstimatedPanelsForConsumption,
  GetPanelSizeOnMapWithRoofAngle,
} from 'utils/calculation/calculation';
import Popup from 'components/Popup';
import DEBUG_FLAGS from 'config/DebugFlags';
import { getPanelOptions } from 'utils/getPanelOptions';
import { ImArrowLeft2, ImArrowRight2 } from 'react-icons/im';
import { MdClose } from 'react-icons/md';
import useGetRegionPrice from 'api/useGetRegionPrice';
import { SimpleModal } from 'components/SimpleModal';
import useStepNavigation from 'utils/hooks/useStepNavigation';
import usePanelList from 'pages/PagePanel/hooks/usePanelList';
import { GeoPoint } from 'schemas/geoPointSchema';
import { PanelLineState } from 'schemas/panelLineStateSchema';
import { AnswersState } from 'schemas/answerStateSchema';
import CurrentPanelAmountIndicator from './CurrentPanelAmountIndicator';
import IconBackground from './IconBackground';

const drawingZoom = 21;

const tips = [
  {
    key: 'map.tuto.step0',
    icon: (
      <IconBackground bgColor="secondaryColor">
        <LightbulbIcon boxSize="15px" />
      </IconBackground>
    ),
  },
  { key: 'map.tuto.step1', icon: <RoofIcon boxSize="20px" /> },
  {
    key: 'map.tuto.step2',
    icon: <PanelLineGrabIcon color="primaryColor" size={25} />,
  },
  {
    key: 'map.tuto.step3',
    icon: (
      <IconBackground>
        <AddPanelsIcon boxSize="15px" />
      </IconBackground>
    ),
  },
  {
    key: 'map.tuto.step4',
    icon: (
      <IconBackground bgColor="secondaryColor">
        <LightbulbIcon boxSize="15px" />
      </IconBackground>
    ),
  },
  {
    key: 'map.tuto.step5',
    icon: (
      <RemovePanelsIcon boxSize="25px" border="1px solid" borderRadius="50px" />
    ),
  },
  {
    key: 'map.tuto.step6',
    icon: (
      <IconBackground>
        <RotatePanelIcon boxSize="15px" ml="2px" mb="2px" />
      </IconBackground>
    ),
  },
];

/**
 * wrapper component for the element that must follow the map
 */
const DrawingArea = forwardRef(
  (
    {
      mapScale,
      children,
    }: {
      lat: number;
      lng: number;
      mapScale: number;
      children: React.ReactNode;
    },
    ref
  ) => (
    <Box
      ref={ref}
      pointerEvents="none"
      transform={`translate(-50%, -50%) scale(${mapScale})`}
      // background="rgb(0, 151, 19, 0.7)" // this will never be applied has this is a wrapper with 0width and 0height
      // width="500px"
      // height="500px"
      role="button"
      tabIndex={0}
      userSelect="none"
      onKeyDown={() => {}}
      // {...props}
    >
      {children}
    </Box>
  )
);

/**
 * This helper return the ratio needed between the "drawing" map level and the current map level
 * To allow the panels to be a the correct size
 */
const getMapScaleValue = (mapZoom: number, lat: number) => {
  const drawingValue = metersPerPx(drawingZoom, lat);
  const zoomValue = metersPerPx(mapZoom, lat);
  return drawingValue / zoomValue;
};

const PageMap = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentRoute = AppRoutes.MAP_SYSTEM;
  const userAdress = useSelector(AddressSelector);
  const addressGeoCode = useSelector(AddressGeoCodeSelector);
  const [skipAlertVisible, setSkipAlertVisible] = useState(false);
  const tooltipContentRef = useRef<HTMLDivElement>(null);
  const mapDetails = useSelector<RootState, AnswersState['mapDetail']>(
    MapDetailSelector
  );
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [tipsCollapsed, setTipsCollapsed] = useState(false);
  const currentTip = tips[currentTipIndex];

  const panelList = usePanelList();
  const panelId = useSelector(PanelSelector);
  const [amtForcedPanelLines, setAmtForcedPanelLines] = useState<number>(0);
  const explicitConsumption = useSelector(ConsumptionSelector);
  const explicitBillAmount = useSelector(BillSelector);
  const postCode = useSelector(AddressPostcodeSelector);
  const regionPrice = useGetRegionPrice(postCode);
  const { navigateToNextStep, navigateToPreviousStep } =
    useStepNavigation(currentRoute);

  const consumption = useMemo(() => {
    if (explicitConsumption !== undefined) return explicitConsumption;
    if (explicitBillAmount && regionPrice)
      return calculateConsumptionBaseOnBill(explicitBillAmount, regionPrice);
    return 0;
  }, [explicitConsumption, explicitBillAmount, regionPrice]);

  const estimatedPanelAmtNeed =
    panelId &&
    getEstimatedPanelsForConsumption(panelId, panelList, consumption);
  // get panels and calculate size depending on roof angle
  const panelOptions = getPanelOptions(panelId, panelList);
  const roofAngle = useSelector(RoofAngleSelector);
  const panelSize = panelOptions ? { ...panelOptions.size } : undefined;
  if (panelOptions && panelSize && roofAngle) {
    panelSize.h = GetPanelSizeOnMapWithRoofAngle(
      roofAngle,
      panelOptions.size.h
    );
  }

  const [currentMapCenter, setCurrentMapCenter] =
    useState<GeoPoint>(addressGeoCode);
  const [calculatedMapCenter, setCalculatedMapCenter] = useState<
    GeoPoint | undefined
  >(undefined);
  const [zoom, setZoom] = useState<number>(drawingZoom);
  const [zooming, setZooming] = useState<boolean>(false);
  const [allowDrag, setAllowDrag] = useState<boolean>(true);
  const canvasHeight = {
    base: 'calc(100vh - 300px)',
    md: 'calc(100vh - 300px)',
  };

  const [panelLines, setPanelLines] = useState<PanelLineState[] | undefined>(
    mapDetails?.panels
  );
  const [selectedPanelLineId, setSelectedPanelLineId] = useState<
    string | undefined
  >();
  const selectedPanelLine = useMemo(
    () => panelLines?.find((pl) => pl.id === selectedPanelLineId),
    [selectedPanelLineId, panelLines]
  );

  const mapScale = addressGeoCode
    ? getMapScaleValue(zoom, addressGeoCode.lat)
    : 1;

  const avgPanel = useMemo(() => {
    if (!panelLines || panelLines.length === 0) return undefined;
    const avgX1 =
      panelLines.reduce((acc, pl) => acc + pl.point1.x, 0) / panelLines.length;
    const avgY1 =
      panelLines.reduce((acc, pl) => acc + pl.point1.y, 0) / panelLines.length;
    const avgX2 =
      panelLines.reduce((acc, pl) => acc + pl.point2.x, 0) / panelLines.length;
    const avgY2 =
      panelLines.reduce((acc, pl) => acc + pl.point2.y, 0) / panelLines.length;
    return {
      id: 'avg',
      hiddenPanels: [],
      point1: {
        x: avgX1,
        y: avgY1,
      },
      point2: {
        x: avgX2,
        y: avgY2,
      },
      orientation: 'vertical',
    } as PanelLineState;
  }, [panelLines]);

  const averagePanelGeoPoint = useMemo(() => {
    if (!avgPanel || !calculatedMapCenter) return undefined;
    return calculatePanelGeoPoint(calculatedMapCenter, drawingZoom, {
      id: 'avg',
      hiddenPanels: [],
      point1: { x: avgPanel.point1.x, y: avgPanel.point1.y },
      point2: { x: avgPanel.point2.x, y: avgPanel.point2.y },
      orientation: 'vertical',
    });
  }, [avgPanel, calculatedMapCenter]);

  const totalPanels = useMemo(() => getNumPanels(panelLines), [panelLines]);
  const {
    isOpen: modalPanelsTooFarIsOpen,
    onOpen: modalPanelsTooFarOnOpen,
    onClose: modalPanelsTooFarOnClose,
  } = useDisclosure();

  const showNextTip = useCallback(() => {
    setCurrentTipIndex((currentTipIndex + 1) % tips.length);
  }, [currentTipIndex]);

  const showPreviousTip = useCallback(() => {
    setCurrentTipIndex((tips.length + currentTipIndex - 1) % tips.length);
  }, [currentTipIndex]);

  // calculated map center
  useEffect(() => {
    if (!calculatedMapCenter && panelLines && panelLines.length > 0) {
      setCalculatedMapCenter(
        calculatePanelGeoPoint(addressGeoCode, zoom, panelLines[0])
      );
    }
  }, [calculatedMapCenter, panelLines, zoom, addressGeoCode]);

  // --------------------- methods ------------------------

  const updateStore = useCallback(
    (newPanelLines: PanelLineState[]) => {
      if (addressGeoCode && newPanelLines) {
        dispatch(
          updateMapDetails({
            lat: (addressGeoCode as GeoPoint).lat,
            lng: (addressGeoCode as GeoPoint).lng,
            panels: newPanelLines,
          })
        );
      }
    },
    [addressGeoCode, dispatch]
  );

  const handlePanelAdd = useCallback(
    (oldPanelLines: PanelLineState[]) => {
      const newPanelLines = [...(oldPanelLines || [])];
      const newPanelId = getUUID();
      const meterPerPixel = metersPerPx(drawingZoom, addressGeoCode.lat);
      const distanceInMeterX =
        getDistanceBetweenTwoPoints(addressGeoCode, {
          lat: addressGeoCode.lat,
          lng: currentMapCenter.lng,
        }) * 1000;
      const distanceInMeterY =
        getDistanceBetweenTwoPoints(addressGeoCode, {
          lat: currentMapCenter.lat,
          lng: addressGeoCode.lng,
        }) * 1000;
      const distanceInPixelX =
        (distanceInMeterX / meterPerPixel) *
        (currentMapCenter.lng > addressGeoCode.lng ? 1 : -1);
      const distanceInPixelY =
        (distanceInMeterY / meterPerPixel) *
        (currentMapCenter.lat < addressGeoCode.lat ? 1 : -1);
      const startPoint = {
        x: -100 + distanceInPixelX,
        y: distanceInPixelY + newPanelLines.length * 1,
      };
      newPanelLines.push({
        id: newPanelId,
        point1: startPoint,
        point2: { x: startPoint.x + 200, y: startPoint.y },
        hiddenPanels: [],
        orientation: 'vertical',
      });
      setSelectedPanelLineId(newPanelId);
      setPanelLines(newPanelLines);
      updateStore(newPanelLines);
    },
    [currentMapCenter, addressGeoCode, updateStore]
  );

  const checkIfPanelsAreTooFar = useCallback(() => {
    if (!panelLines || panelLines.length === 0) return false;

    const distance =
      getDistanceBetweenTwoPoints(currentMapCenter, averagePanelGeoPoint) *
      1000;
    if (distance > 100) return true;
    return false;
  }, [panelLines, currentMapCenter, averagePanelGeoPoint]);

  const handlePanelAddClick = useCallback(() => {
    if (checkIfPanelsAreTooFar()) {
      modalPanelsTooFarOnOpen();
      return;
    }
    handlePanelAdd(panelLines);
  }, [
    panelLines,
    checkIfPanelsAreTooFar,
    modalPanelsTooFarOnOpen,
    handlePanelAdd,
  ]);

  const handlePanelsTooFarConfirmation = useCallback(() => {
    // TODO: delete all panels first
    modalPanelsTooFarOnClose();
    handlePanelAdd([]);
  }, [handlePanelAdd, modalPanelsTooFarOnClose]);

  const handlePanelLineDelete = () => {
    if (!selectedPanelLineId) {
      throw new Error(
        'we should not be able to delete panel without selected panel..'
      );
      return;
    }
    const newPanelLines =
      panelLines?.filter((pl) => pl.id !== selectedPanelLineId) || [];
    setPanelLines(newPanelLines);
    setSelectedPanelLineId(undefined);
    updateStore(newPanelLines);
  };

  const handleContinue = () => {
    dispatch(updateMapDetails(mapDetails));

    navigateToNextStep();
  };

  const handleSkipConfirm = () => {
    dispatch(updateMapDetails(undefined));

    navigateToNextStep();
  };
  const handleSkip = () => {
    if (mapDetails) {
      setSkipAlertVisible(true);
      return;
    }
    handleSkipConfirm();
  };

  const onGoogleMapChange = (change: GoogleMapReact.ChangeEventValue) => {
    setCurrentMapCenter(change.center);
    setZoom(change.zoom);
  };

  const handlePanelLineChange = (pState: PanelLineState) => {
    if (!panelLines || pState.angle === undefined) {
      throw new Error(
        `we should never update store with no angle or no panels for panel line:${JSON.stringify(
          pState
        )}`
      );
      return;
    }

    const newPanelLines = panelLines.map((panel) =>
      panel.id === pState.id ? pState : panel
    );
    setPanelLines(newPanelLines);
    setSelectedPanelLineId(pState.id);
    updateStore(newPanelLines);
  };

  const handlePanelLineSelect = (panelLineId: string) => {
    setSelectedPanelLineId(panelLineId);
  };

  const changePanelOrientation = () => {
    if (selectedPanelLineId) {
      const newLines = structuredClone(panelLines) as PanelLineState[];
      newLines.forEach((l) => {
        if (l.id === selectedPanelLineId) {
          l.orientation =
            l.orientation !== 'horizontal' ? 'horizontal' : 'vertical';
        }
      });
      setPanelLines(newLines);
    }
  };

  // --------------------- effects ------------------------

  // tooltip animation
  useEffect(() => {
    const element = tooltipContentRef.current;
    gsap.to(element, {
      autoAlpha: 1,
      duration: 0.5,
      scaleX: 1,
      startAt: {
        autoAlpha: 0,
        scaleX: 0.9,
      },
    });
    const timeout = setTimeout(() => {
      showNextTip();
    }, 1000 * config.options.map.tipInterval);

    return () => {
      clearTimeout(timeout);
      gsap.killTweensOf(element);
    };
  }, [currentTipIndex, showNextTip]);

  // case no panel lines, automatically add one in the center
  useEffect(() => {
    if (
      (mapDetails !== undefined && mapDetails.panels.length > 0) ||
      amtForcedPanelLines > 0
    )
      return;
    handlePanelAdd(panelLines);
    setAmtForcedPanelLines((old) => old + 1);
    // select first line
    if (
      mapDetails?.panels &&
      mapDetails.panels.length > 0 &&
      !selectedPanelLineId
    ) {
      setSelectedPanelLineId(mapDetails?.panels[0].id);
    }
  }, [
    panelLines,
    mapDetails,
    handlePanelAdd,
    setSelectedPanelLineId,
    selectedPanelLineId,
    amtForcedPanelLines,
  ]);

  // --------------------- effects ------------------------

  // --------------------- render ------------------------

  if (!userAdress) {
    // TODO: what we do in this case?
    return <div>NO USER ADDRESS</div>;
  }

  return (
    <PageLayout
      buttonLabel={t('common.continue')}
      buttonCallback={handleContinue}
      backButton
      backCallback={navigateToPreviousStep}
      buttonDisabled={totalPanels <= 0}
      skipButtonLabel={t('common.skip')}
      skipButtonCallback={handleSkip}
    >
      <SimpleModal
        isOpen={modalPanelsTooFarIsOpen}
        onClose={modalPanelsTooFarOnClose}
        title={t('common.warning')}
        description={t('map.modal.panelsTooFar.description')}
        button={
          <Flex width="full" justifyContent="right" gap={2}>
            <ChakraButton onClick={modalPanelsTooFarOnClose} color="black">
              {t('common.cancel')}
            </ChakraButton>
            <Button onClick={handlePanelsTooFarConfirmation}>
              {t('common.continue')}
            </Button>
          </Flex>
        }
        modalContentProps={{
          maxWidth: { base: '90vw', md: '600px' },
        }}
      />

      <Box>
        <Box
          width="100vw"
          height={canvasHeight} // Important! Always set the container height explicitly for google map to work
          position="relative"
        >
          <Popup
            isOpen={skipAlertVisible}
            title={t('map.skip.confirm.title')}
            description={t('map.skip.confirm.description')}
            onContinue={handleSkipConfirm}
            continueLabel={t('map.skip.confirm.continue')}
            onClose={() => {
              setSkipAlertVisible(false);
            }}
            cancelLabel={t('map.skip.confirm.cancel')}
          />

          <Box
            position="absolute"
            zIndex={10}
            width="full"
            height={canvasHeight}
            pointerEvents="none"
            sx={{ touchAction: 'none' }}
            display="flex"
            alignItems="start"
            justifyContent="center"
          >
            {/* north icon */}
            <NorthIcon
              position="absolute"
              left="0"
              right="0"
              ml="auto"
              mr="auto"
            />

            {/* top icons */}
            <HStack p="4" width="fit-content" pt="20px" alignItems="start">
              {/* left: Num panels */}
              <HStack
                right="50%"
                position="absolute"
                pr={{ base: '25px', md: '50px' }}
                pointerEvents="auto"
                alignItems="start"
                gap={{ base: '5px', md: '15px' }}
              >
                <Tooltip
                  hasArrow
                  bg="secondaryColor"
                  label={t('MAP_SYSTEM.add_panel.tooltip')}
                >
                  <IconButton
                    aria-label="add panels"
                    boxSize={{ base: '55px', md: '65px' }}
                    borderRadius="full"
                    icon={
                      <AddPanelsIcon boxSize={{ base: '30px', md: '35px' }} />
                    }
                    onClick={handlePanelAddClick}
                    shadow="dark-lg"
                  />
                </Tooltip>
                <CurrentPanelAmountIndicator
                  totalPanels={totalPanels}
                  estimatedPanelAmtNeed={estimatedPanelAmtNeed}
                />
              </HStack>

              {/* right: panels */}
              <HStack
                left="50%"
                pl="50px"
                position="absolute"
                pointerEvents="auto"
                alignItems="start"
              >
                {/** CHANGE ORIENTATION PANELS */}
                <Tooltip
                  label={
                    selectedPanelLine?.orientation === 'vertical'
                      ? t('MAP_SYSTEM.rotate_H_panel.tooltip')
                      : t('MAP_SYSTEM.rotate_V_panel.tooltip')
                  }
                  hasArrow
                  bg="secondaryColor"
                >
                  <IconButton
                    disabled={!selectedPanelLineId}
                    boxSize={46}
                    aria-label="change panel orientation"
                    icon={<RotatePanelIcon boxSize="30px" pl="5px" pb="4px" />}
                    onClick={changePanelOrientation}
                    transform="auto"
                    rotate={
                      selectedPanelLine?.orientation !== 'vertical' ? -90 : 0
                    }
                    shadow="dark-lg"
                  />
                </Tooltip>

                {/** DELETE PANELS */}
                <Tooltip
                  label={t('MAP_SYSTEM.delete_panel.tooltip')}
                  hasArrow
                  bg="secondaryColor"
                >
                  <IconButton
                    disabled={
                      !selectedPanelLineId ||
                      (panelLines && panelLines.length < 1)
                    }
                    boxSize={46}
                    aria-label="remove panels"
                    icon={
                      <RemovePanelsIcon
                        boxSize={46}
                        // border="solid 2px #ffffff77"
                        borderRadius="25px"
                      />
                    }
                    onClick={handlePanelLineDelete}
                    shadow="dark-lg"
                  />
                </Tooltip>
              </HStack>
            </HStack>

            {/* // INFO BOX */}
          </Box>

          {
            // TODO: IMPORTANT!! we should use the Map component here to avoid dupplication of code!
            addressGeoCode && (
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: config.GOOGLE_API_KEY,
                  libraries: ['visualization', 'places', 'drawing'],
                  id: '__googleMapsScriptId',
                }}
                center={(calculatedMapCenter || addressGeoCode) as Coords}
                defaultZoom={drawingZoom}
                onChange={onGoogleMapChange}
                onZoomAnimationStart={() => setZooming(true)}
                onZoomAnimationEnd={() => setZooming(false)}
                options={(_maps) => ({
                  // DOC OPTIONS: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions.clickableIcons
                  mapTypeId: 'satellite',
                  disableDefaultUI: true,
                  zoomControl: false,
                  keyboardShortcuts: false,
                  zoomControlOptions: { position: 8 },
                  fullscreenControl: false,
                  gestureHandling: allowDrag ? 'auto' : 'none',
                  minZoom: 19,
                  rotateControl: false,
                  tilt: 0,
                })}
              >
                {addressGeoCode && panelSize && (
                  <DrawingArea
                    lat={addressGeoCode.lat}
                    lng={addressGeoCode.lng}
                    mapScale={mapScale}
                  >
                    <Box
                      visibility={!zooming ? 'visible' : 'hidden'}
                      onMouseDown={() => setAllowDrag(false)}
                      onTouchStart={() => setAllowDrag(false)}
                      onMouseUp={() => setAllowDrag(true)}
                      onTouchEnd={() => setAllowDrag(true)}
                    >
                      {panelLines?.map((panelLine) => (
                        <PanelLine
                          key={panelLine.id + panelLine.orientation} // force updating panel also on orientation change
                          scaleRatio={mapScale}
                          initialState={panelLine}
                          metersPerPixel={metersPerPx(
                            drawingZoom,
                            addressGeoCode.lat
                          )}
                          selected={panelLine.id === selectedPanelLineId}
                          panelSizeInMeter={panelSize}
                          onSelect={handlePanelLineSelect}
                          onChangeEnd={handlePanelLineChange}
                        />
                      ))}
                      {DEBUG_FLAGS.SHOW_MAP_CENTER && (
                        <Box
                          bgColor="red"
                          borderRadius="10"
                          width="5"
                          height="5"
                        />
                      )}
                    </Box>
                  </DrawingArea>
                )}

                {calculatedMapCenter && (
                  <DrawingArea
                    lat={calculatedMapCenter.lat}
                    lng={calculatedMapCenter.lng}
                    mapScale={mapScale}
                  >
                    {DEBUG_FLAGS.SHOW_MAP_CENTER && (
                      <Box
                        bgColor="blue"
                        borderRadius="10"
                        width="5"
                        height="5"
                      />
                    )}
                  </DrawingArea>
                )}
                {currentMapCenter && (
                  <DrawingArea
                    lat={currentMapCenter.lat}
                    lng={currentMapCenter.lng}
                    mapScale={mapScale}
                  >
                    {DEBUG_FLAGS.SHOW_MAP_CENTER && (
                      <Box
                        bgColor="green"
                        borderRadius="10"
                        width="5"
                        height="5"
                      />
                    )}
                  </DrawingArea>
                )}

                {averagePanelGeoPoint && (
                  <DrawingArea
                    lat={averagePanelGeoPoint.lat}
                    lng={averagePanelGeoPoint.lng}
                    mapScale={mapScale}
                  >
                    {DEBUG_FLAGS.SHOW_MAP_CENTER && (
                      <Box
                        bgColor="purple"
                        borderRadius="10"
                        width="5"
                        height="5"
                      />
                    )}
                  </DrawingArea>
                )}
              </GoogleMapReact>
            )
          }
        </Box>

        {/* // info bubble */}
        <Center
          width="full"
          position="relative"
          bottom={{ base: '-5px', md: '-20px' }}
          height="0"
        >
          <Center
            bottom={0}
            position="absolute"
            w="full"
            width={{ base: '90%', md: '500px' }}
          >
            {tipsCollapsed && (
              <Button
                onClick={() => setTipsCollapsed(!tipsCollapsed)}
                shadow="base"
                fontSize="sm"
                pt="5px !important"
                pb="2px !important"
                px="15px !important"
                bgColor="white"
                color="primaryColor"
              >
                {t('map.tuto.help')}
              </Button>
            )}
            {!tipsCollapsed && (
              <ScaleFade in={!tipsCollapsed}>
                <Box
                  background="white"
                  borderRadius={10}
                  px={{ base: '8px', md: '8px' }}
                  pt="15px"
                  pb="0px"
                  boxShadow="base"
                  position="relative"
                >
                  <IconButton
                    position="absolute"
                    top="-5px"
                    right="-5px"
                    variant="unstyled"
                    aria-label="close-tips"
                    color="grey"
                    _hover={{}}
                    _active={{}}
                    icon={<MdClose />}
                    onClick={() => setTipsCollapsed(!tipsCollapsed)}
                  />
                  <HStack
                    ref={tooltipContentRef}
                    width="full"
                    fontSize={{ base: 'sm', md: 'md' }}
                    px="5px"
                    pt="5px"
                    alignItems="flex-start"
                    pointerEvents="none"
                    userSelect="none"
                  >
                    {currentTip.icon}
                    <span>
                      <Trans
                        i18nKey={currentTip.key}
                        values={{
                          consumption,
                          panel_amount: estimatedPanelAmtNeed,
                        }}
                        components={{
                          green: (
                            <p
                              style={{
                                color: 'var(--chakra-colors-primaryColor)',
                                display: 'inline',
                              }}
                            />
                          ),
                        }}
                      />
                    </span>
                  </HStack>

                  <HStack
                    w="full"
                    pt="0px"
                    justifyContent="center"
                    gap="5px"
                    userSelect="none"
                  >
                    <Button color="primaryColor" variant="icon">
                      <ImArrowLeft2
                        fontSize="20px"
                        onClick={() => showPreviousTip()}
                      />
                    </Button>
                    <Spacer />
                    {tips.map((ti, i) => (
                      <Box
                        key={ti.key}
                        borderRadius="full"
                        bgColor={
                          i === currentTipIndex
                            ? 'primaryColor'
                            : 'reno.grey100'
                        }
                        w="8px"
                        h="8px"
                      />
                    ))}
                    <Spacer />
                    <Button
                      color="primaryColor"
                      variant="icon"
                      onClick={() => showNextTip()}
                    >
                      <ImArrowRight2 fontSize="20px" />
                    </Button>
                  </HStack>
                </Box>
              </ScaleFade>
            )}
          </Center>
        </Center>
      </Box>
    </PageLayout>
  );
};

export default PageMap;
